import { memo, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import type { FamilyMember, Category } from "@/types";
import { Modal } from "@/components/Modal/Modal";
import ListOptions from "./ListOptions";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useClosetStore } from "@/store";
import Logo from "../../assets/logo.png";
import styles from "./Sidebar.module.css";

interface SidebarProps {
  members: FamilyMember[];
  categories: Category[];
  activeMemberId: string;
  activeCategoryName: string | null;
  disabled?: boolean;
  onShare: () => void;
  onLogout: () => void;
}

export const Sidebar = memo(({ members, activeMemberId, onShare, onLogout, disabled }: SidebarProps) => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const {
    getAllCategories,
    getAttributesFilterOptions,
    categories,
    setActiveCategory,
    setActiveAttribute,
    isLoadingCategories,
    isLoadingAttributesFilterOptions,
    filter,
    attributesFilterOptions,
    loadingInitialPage
  } = useClosetStore();

  useEffect(() => {
    getAllCategories();
    getAttributesFilterOptions();
  }, []);

  const selectedCategories = filter?.selectedCategories;
  const selectedAttribute = filter.selectedAttributes;

  const collections = useMemo(
    () =>
      _.find(attributesFilterOptions?.product_attributes || [], (att) => att.name === "Collection")?.values.sort(
        (a, b) => (a.name < b.name ? 1 : -1)
      ) || [],
    [attributesFilterOptions]
  );

  return (
    <>
      <aside className={styles.sidebar}>
        <div className={styles.logoWrap}>
          {!isMobile ? (
            <span className={styles.logo}>Closai</span>
          ) : (
            <img src={Logo} alt="Closai Logo" className={styles.roundedLogo} />
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.label}>Closets</div>
          {members.map((member) => (
            <button
              key={member.id}
              className={[styles.item, activeMemberId === member.id ? styles.itemActive : ""].filter(Boolean).join(" ")}
              // onClick={() => onMemberSelect(member.id)}
            >
              <span className={styles.avatar} style={{ background: member.color }}>
                {member.icon}
              </span>
              <span className={styles.itemText}>{member.name}</span>
            </button>
          ))}
        </div>

        {!isLoadingAttributesFilterOptions && !!collections.length && (
          <ListOptions
            data={collections}
            name="Collections"
            onChange={(id) => setActiveAttribute(id, "Collections")}
            selectedValues={selectedAttribute}
          />
        )}

        <div className={styles.divider} />

        {!isLoadingCategories && !!categories.length && (
          <div className={styles.categoriesContainer}>
            <ListOptions
              data={categories}
              name="Departments"
              onChange={setActiveCategory}
              selectedValues={selectedCategories}
              disableValues={loadingInitialPage}
            />
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.footerBtn} disabled={disabled} onClick={onShare}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            <span className={styles.footerText}>Share</span>
          </button>
          <button className={[styles.footerBtn, styles.logoutBtn].join(" ")} onClick={() => setShowLogoutConfirm(true)}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span className={styles.footerText}>Log out</span>
          </button>
          <a href="mailto:contact@closai.io" className={styles.supportLink}>
            Need help? Contact us at contact@closai.io
          </a>
        </div>
      </aside>

      <Modal open={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)} title="Log out" maxWidth={400}>
        <p className={styles.logoutMessage}>Are you sure you want to log out?</p>
        <div className={styles.logoutActions}>
          <button className={styles.cancelBtn} onClick={() => setShowLogoutConfirm(false)}>
            Cancel
          </button>
          <button
            className={styles.confirmLogoutBtn}
            onClick={() => {
              setShowLogoutConfirm(false);
              onLogout();
            }}
          >
            Log out
          </button>
        </div>
      </Modal>
    </>
  );
});
